import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useState } from "react";
import { ProfileUI } from "../BoxUI";
import useResponsive from "../../hooks/useResponsive";
import MedicPersonnelShift from "./MedicPersonnelShift";
import { MedicInformationProps } from "../../interface/medic";
import {
  formatDate,
  formatFullName,
  formatTextType,
  getUrlPathId,
} from "../../utils/url-id";

import { Link, useLocation, useNavigate } from "react-router-dom";
import MedicStats from "./medicStats";
import { labelStyles, textSty } from "../UIElements/styles-input";
import MedicAppraisal from "./medicAppraisal";
import { btnSty } from "../ui-styles";
import { PATH_MEDIC } from "../../routes/path";
import { VerifyMedicModal } from "./modal-contents";
import MedicMobilityScore from "./MedicMobilityScore";

const MedicDetailsContent: FC<{
  medicInfo: MedicInformationProps;
  handleRefresh: () => void;
  loading: boolean;
}> = ({ medicInfo, loading, handleRefresh }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const navigate = useNavigate();

  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  const [mode, setMode] = useState("timesheet");
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = () => setOpenModal(!openModal);

  return (
    <>
      <VerifyMedicModal
        open={openModal}
        modalHandler={handleOpenModal}
        handleRefresh={handleRefresh}
      />
      <MedicStats />
      <Box
        sx={{
          margin: 3,
          padding: isMedium ? 3 : 4,
          borderRadius: "3px",
          minHeight: "100%",
          backgroundColor: theme.palette.common.white,
        }}
      >
        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
            Personnel Profile
          </Typography>
        </Stack>
        {!loading ? (
          <>
            <ProfileUI
              name={formatFullName(medicInfo?.first_name, medicInfo.last_name)}
              email={medicInfo.email}
              staffId={medicInfo._id}
              folio_number={medicInfo.folio_no}
            />
            <Stack
              direction={"row"}
              alignItems={"center"}
              flexWrap={"wrap"}
              sx={{
                my: 3,
                gap: 4,
                backgroundColor: "#F7F8F9",
                borderRadius: "4",
                p: 3,
              }}
            >
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Gender
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {formatTextType(medicInfo?.gender) ?? "-"}
                </Typography>
              </Stack>

              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Date of Birth
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {formatDate(medicInfo?.dob) ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Medic Type
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {formatTextType(medicInfo?.medic_type) ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Contact
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.phone_number ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Role
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.role ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Years of Experience
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.years_of_experience ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Is Verified
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {`${medicInfo?.isVerified}`}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Verification
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {`${medicInfo?.verification ?? "-"}`}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  State
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.state ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  City
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.city ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Address
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.address ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Affiliated Council
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.affiliated_council ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Device
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.device ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Level
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.level ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Facility name
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.employment_details?.facility_info?.name ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Facility State
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.employment_details?.facility_info?.state ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Facility city
                </Typography>
                <Typography
                  sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                >
                  {medicInfo?.employment_details?.facility_info?.city ?? "-"}
                </Typography>
              </Stack>
              <Stack sx={{}}>
                <Typography
                  sx={{
                    ...labelStyles,
                    mb: 0,
                    color: theme.palette.neutral.contrastText,
                    fontFamily: "Lexend",
                    my: 3,
                  }}
                >
                  Documents
                </Typography>
                <Stack>
                  <Typography
                    sx={{
                      ...textSty,
                      fontFamily: "Lexend",
                      fontWeight: 500,
                      mb: 2,
                    }}
                  >
                    Annual practicing licence -{" "}
                    {medicInfo?.documents?.Annual_practicing_license && (
                      <a
                        href={
                          medicInfo?.documents?.Annual_practicing_license ?? ""
                        }
                        target="_blank"
                      >
                        {medicInfo?.documents?.Annual_practicing_license ?? ""}
                      </a>
                    )}
                  </Typography>
                  {medicInfo?.documents?.NYSC_discharge && (
                    <Typography
                      sx={{
                        ...textSty,
                        fontFamily: "Lexend",
                        fontWeight: 500,
                        mb: 2,
                      }}
                    >
                      NYSC discharge -{" "}
                      <a
                        href={medicInfo?.documents?.NYSC_discharge ?? ""}
                        target="_blank"
                      >
                        {medicInfo?.documents?.NYSC_discharge ?? ""}
                      </a>
                    </Typography>
                  )}
                  {medicInfo?.documents?.Full_registration && (
                    <Typography
                      sx={{ ...textSty, fontFamily: "Lexend", fontWeight: 500 }}
                    >
                      Full registration -{" "}
                      <a
                        href={medicInfo?.documents?.Full_registration ?? ""}
                        target="_blank"
                      >
                        {medicInfo?.documents?.Full_registration ?? ""}
                      </a>
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </>
        ) : (
          <>
            <Skeleton height={120} />
            <Skeleton height={120} />
          </>
        )}

        {/* <Stack>
          <Typography sx={headerSty} variant="subtitle2">
            Recent Activity
          </Typography>
          <MedicRecentActivity />
        </Stack> */}

        {/*  */}
        <Box
          display={"flex"}
          flexDirection={isMedium ? "column-reverse" : "row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 3, gap: 3 }}
        >
          <Stack
            direction={"row"}
            sx={{
              gap: 1,
              background: "#284CB133",
              p: 1,
              borderRadius: "12px",
              my: 2,
            }}
          >
            <Button
              sx={{
                ...btnSty,
                color: mode === "timesheet" ? "#fff" : "#1E1E1E",
                background:
                  mode === "timesheet" ? theme.palette.primary.main : "",
              }}
              onClick={() => setMode("timesheet")}
            >
              Timesheet
            </Button>
            <Button
              sx={{
                ...btnSty,
                color: mode === "appraisal" ? "#fff" : "#1E1E1E",
                background:
                  mode === "appraisal" ? theme.palette.primary.main : "",
              }}
              onClick={() => setMode("appraisal")}
            >
              Appraisal
            </Button>
          </Stack>
          <Stack direction={isSmall ? "column" : "row"} spacing={3}>
            {medicInfo?.verification === "vetted" ||
              (medicInfo?.verification === "pending" ? (
                <></>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleOpenModal}
                  sx={{ width: 150 }}
                >
                  Verify Medic
                </Button>
              ))}
            <Button
              variant="contained"
              sx={{
                width: 180,
              }}
              onClick={() =>
                navigate(
                  `${PATH_MEDIC.root}/${getUrlPathId(
                    location.pathname,
                  )}/employment`,
                )
              }
            >
              Medic Employment
            </Button>
          </Stack>
        </Box>
        {mode === "timesheet" ? (
          <Stack>
            <Typography sx={headerSty} variant="subtitle2">
              Personnel shift history
            </Typography>
            <MedicPersonnelShift />
          </Stack>
        ) : (
          <Stack>
            <Typography sx={headerSty} variant="subtitle2">
              Personnel Appraisal
            </Typography>
            <MedicAppraisal />
          </Stack>
        )}

        <Stack sx={{ my: 4 }}>
          <Typography sx={headerSty} variant="subtitle2">
            Mobility Score
          </Typography>
          <MedicMobilityScore />
        </Stack>
      </Box>
    </>
  );
};

export default MedicDetailsContent;
